import {
    useCallback,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type FormEvent,
    type ReactElement,
} from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useListInsertion } from "@tokens/index";
import { IconButton } from "@primitives/IconButton";
import { Dialog } from "@primitives/overlay";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import type { QueueSummary } from "../../../experience-shell/queues/types";
import { ComposeFooter } from "./ComposeFooter";
import { ComposeRow } from "./ComposeRow";
import { FromSelector } from "./FromSelector";
import { removeMention, type MentionRange } from "./mention";
import { optionDomId } from "./optionDomId";
import { QuestionField } from "./QuestionField";
import { RecipientField } from "./RecipientField";
import { RecipientListbox } from "./RecipientListbox";
import { resolveDefaultSenderId, sendAsOptions, summarizeRecipientCounts } from "./recipients";
import type { AskQuestionDialogProps } from "./types";
import { useQueueLookups } from "./useQueueLookups";
import { useRecipientSearch } from "./useRecipientSearch";
import styles from "./AskQuestionDialog.module.css";

/**
 * AskQuestionDialog: compose grammar. To, From (when the asker can send from
 * more than one queue), then the question as the hero.
 *
 * One recipient list serves both the "To" field and the `@` mention in the
 * question; `source` says which one owns it. All results come from the
 * in-memory queue tree, so no frame waits on data.
 */

type ListSource = "to" | "mention" | null;

const EMPTY_IDS: readonly string[] = [];

export function AskQuestionDialog({
    isOpen,
    isSubmitting,
    errorMessage,
    queueTree,
    excludeQueueIds,
    originatingQueueId,
    sendAsQueues,
    initialRecipientQueueIds,
    onClose,
    onSubmit,
}: AskQuestionDialogProps): ReactElement {
    const { isCompact } = useViewport();
    const baseId = useId();
    const id = (suffix: string): string => `${baseId}-${suffix}`;
    const [toInputId, listId] = [id("to"), id("list")] as const;
    const [bodyId, fromLabelId, errorId] = [id("body"), id("from"), id("error")] as const;

    const toInputRef = useRef<HTMLInputElement>(null);
    const toFrameRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const pendingCaret = useRef<number | null>(null);

    const initialIds = initialRecipientQueueIds ?? EMPTY_IDS;
    const [recipientIds, setRecipientIds] = useState<readonly string[]>(initialIds);
    const [toQuery, setToQuery] = useState("");
    const [toListOpen, setToListOpen] = useState(false);
    const [body, setBody] = useState("");
    const [mention, setMention] = useState<MentionRange | null>(null);
    const [dismissedMentionAt, setDismissedMentionAt] = useState<number | null>(null);
    const [senderId, setSenderId] = useState<string | undefined>(undefined);
    const [session, setSession] = useState(0);

    // Reset during render on open, so the first committed frame is fresh.
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            setRecipientIds(initialIds);
            setToQuery("");
            setToListOpen(false);
            setBody("");
            setMention(null);
            setDismissedMentionAt(null);
            setSenderId(undefined);
            setSession((s) => s + 1);
        }
    }

    const { allWorkable, resolveQueueName, labelById, personalQueueIds } =
        useQueueLookups(queueTree);

    const senderOptions = useMemo(() => sendAsOptions(sendAsQueues), [sendAsQueues]);
    const effectiveSenderId = senderId ?? resolveDefaultSenderId(senderOptions, originatingQueueId);
    const showFrom = senderOptions.length > 1;

    const mentionActive = mention !== null && dismissedMentionAt !== mention.start;
    const source: ListSource = mentionActive ? "mention" : toListOpen ? "to" : null;
    const query = mentionActive ? mention.query : toQuery;
    const search = useRecipientSearch(allWorkable, recipientIds, excludeQueueIds, query);

    const canSubmit = !isSubmitting && recipientIds.length > 0 && body.trim().length > 0;
    const summary = summarizeRecipientCounts(recipientIds, personalQueueIds);

    useLayoutEffect(() => {
        const caret = pendingCaret.current;
        const el = textareaRef.current;
        if (caret === null || el === null) return;
        pendingCaret.current = null;
        el.setSelectionRange(caret, caret);
    }, [body]);

    const addRecipient = useCallback(
        (queue: QueueSummary) => {
            setRecipientIds((prev) => (prev.includes(queue.id) ? prev : [...prev, queue.id]));
            if (mentionActive && mention !== null) {
                const next = removeMention(body, mention);
                pendingCaret.current = next.caret;
                setBody(next.text);
                setMention(null);
                textareaRef.current?.focus();
                return;
            }
            // The list stays open and filters in place while the field has
            // focus and candidates remain, so a second pick does not rebuild
            // it. It closes when nothing is left to pick.
            setToQuery("");
            const excluded = new Set(excludeQueueIds ?? []);
            const remaining = allWorkable.some(
                (q) => q.id !== queue.id && !recipientIds.includes(q.id) && !excluded.has(q.id),
            );
            setToListOpen(remaining);
            toInputRef.current?.focus();
        },
        [allWorkable, body, excludeQueueIds, mention, mentionActive, recipientIds],
    );

    const browseRecipients = useCallback(() => {
        setToQuery("");
        setToListOpen(true);
        toInputRef.current?.focus();
    }, []);

    const removeRecipient = useCallback((queueId: string) => {
        setRecipientIds((prev) => prev.filter((id) => id !== queueId));
    }, []);

    const pickHighlighted = useCallback((): boolean => {
        if (search.highlighted === undefined) return false;
        addRecipient(search.highlighted);
        return true;
    }, [addRecipient, search.highlighted]);

    const dismissMention = useCallback(() => {
        if (mention !== null) setDismissedMentionAt(mention.start);
    }, [mention]);

    const closeList = useCallback(() => {
        if (mentionActive) dismissMention();
        else setToListOpen(false);
    }, [dismissMention, mentionActive]);

    const handleMentionChange = useCallback((next: MentionRange | null) => {
        setMention(next);
        // A new token at another position clears the dismissal.
        setDismissedMentionAt((at) => (next === null || at === next.start ? at : null));
    }, []);

    const handleSubmit = useCallback(() => {
        if (!canSubmit) return;
        onSubmit({
            workflowId: crypto.randomUUID(),
            recipientQueueIds: recipientIds,
            body: body.trim(),
            ...(effectiveSenderId !== undefined ? { originatingQueueId: effectiveSenderId } : {}),
        });
    }, [canSubmit, onSubmit, recipientIds, body, effectiveSenderId]);

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        handleSubmit();
    };

    // One error row, not a list: framer measures its `auto` height once.
    const insertion = useListInsertion("auto");
    const initialFocusRef = initialIds.length > 0 ? textareaRef : toInputRef;
    const highlightedOptionId =
        search.highlighted !== undefined ? optionDomId(listId, search.highlighted.id) : undefined;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title="Ask a question"
            size="lg"
            presentation="auto"
            bodyPadding="none"
            initialFocusRef={initialFocusRef}
            as="form"
            onSubmit={handleFormSubmit}
            footer={
                <ComposeFooter
                    summary={summary}
                    canSend={canSubmit}
                    isSubmitting={isSubmitting}
                    compact={isCompact}
                    onCancel={onClose}
                />
            }
            testId="ask-question-dialog"
        >
            <div key={session} className={styles.form}>
                <ComposeRow
                    label="To"
                    htmlFor={toInputId}
                    trailing={
                        <IconButton
                            type="button"
                            icon={Plus}
                            aria-label="Add person or queue"
                            tooltip="Add person or queue"
                            variant="ghost"
                            size="sm"
                            onClick={browseRecipients}
                        />
                    }
                >
                    <RecipientField
                        inputId={toInputId}
                        listId={listId}
                        inputRef={toInputRef}
                        frameRef={toFrameRef}
                        recipientIds={recipientIds}
                        labelById={labelById}
                        personalQueueIds={personalQueueIds}
                        query={toQuery}
                        onQueryChange={setToQuery}
                        listOpen={source === "to"}
                        onListOpenChange={setToListOpen}
                        highlighted={source === "to" ? search.highlighted : undefined}
                        onMoveHighlight={search.move}
                        onAdd={addRecipient}
                        onRemove={removeRecipient}
                    />
                </ComposeRow>
                {showFrom && (
                    <ComposeRow label="From" labelId={fromLabelId} className={styles.fromRow}>
                        <FromSelector
                            labelId={fromLabelId}
                            options={senderOptions}
                            value={effectiveSenderId ?? ""}
                            onChange={setSenderId}
                        />
                    </ComposeRow>
                )}
                <div className={styles.question}>
                    <QuestionField
                        id={bodyId}
                        textareaRef={textareaRef}
                        listId={listId}
                        value={body}
                        onChange={setBody}
                        onMentionChange={handleMentionChange}
                        mentionOpen={source === "mention"}
                        activeOptionId={highlightedOptionId}
                        onMoveHighlight={search.move}
                        onPickHighlighted={pickHighlighted}
                        onDismissMention={dismissMention}
                        onSend={handleSubmit}
                        describedBy={errorMessage !== null ? errorId : undefined}
                    />
                </div>
                {errorMessage !== null && (
                    <motion.div className={styles.error} {...insertion}>
                        <div className={styles.errorInner}>
                            <Text as="p" id={errorId} role="bodySm" tone="danger">
                                <span role="alert">{errorMessage}</span>
                            </Text>
                        </div>
                    </motion.div>
                )}
            </div>
            <RecipientListbox
                id={listId}
                open={source !== null}
                anchorRef={source === "mention" ? textareaRef : toFrameRef}
                matchAnchorWidth={source !== "mention"}
                candidates={search.candidates}
                highlightedId={search.highlighted?.id}
                loading={queueTree === undefined}
                resolveQueueName={resolveQueueName}
                onPick={addRecipient}
                onHighlight={search.highlightId}
                onClose={closeList}
            />
        </Dialog>
    );
}
